/**
 * Admin Profile Drawer — slides in from the right when an admin row is clicked.
 *
 * 5 tabs:
 *   1. Profile           — name, role, active, timezone
 *   2. Capabilities      — bundle picker + per-capability checkboxes
 *   3. Support identity  — the 3 columns from migration 1508 + signature
 *   4. Sign-in           — where 2-step verification lives, open sessions,
 *                          "Sign out everywhere" (routers/admin_sessions.py)
 *   5. Audit log         — recent admin_audit_log entries
 *
 * All edits go through PUT /api/admin/team/:id, POST /api/admin/team/:id/capabilities/set,
 * POST /api/admin/team/:id/bundle/apply. Sessions: GET /api/admin/sessions/admin/:id,
 * POST /api/admin/sessions/admin/:id/revoke-all.
 */

import React, { useEffect, useState } from 'react';
import {
  Activity, AlertCircle, CheckCircle2, Edit3, KeyRound, LogOut, Mail,
  Save, Shield, User, X,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';
import { errorMessage } from '../settings/settingsLogic';
import { SessionsList } from '../me/SessionsList';
import { SessionsResponse } from '../me/sessionsLogic';
import { profileChanges } from './teamProfileLogic';

type TabKey = 'profile' | 'capabilities' | 'support' | 'signin' | 'audit';

interface AdminDetail {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  permissions: { capabilities?: string[]; bundle_applied?: string } | null;
  active: boolean;
  two_factor_enabled: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  locked_until: string | null;
  support_from_email: string | null;
  support_from_display_name: string | null;
  support_signature_html: string | null;
  slack_handle: string | null;
  timezone: string | null;
  notification_preferences: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface Bundle {
  name: string;
  description: string;
  role: string;
  capability_count: number;
  capabilities: string[];
}

interface CapEntry {
  key: string;
  high_blast: boolean;
}

interface AuditEntry {
  id: string;
  action_type: string;
  target_resource: string | null;
  action_details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  created_at: string;
}

export const AdminProfileDrawer: React.FC<{
  adminId: string;
  onClose: () => void;
}> = ({ adminId, onClose }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('profile');

  const { data, isLoading } = useQuery<{ data: AdminDetail }>({
    queryKey: ['admin-team-detail', adminId],
    queryFn: () => adminFetch(`/api/admin/team/${adminId}`),
    enabled: !!adminId,
  });
  const admin = data?.data;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 truncate">
              {admin?.full_name || admin?.email || '…'}
            </h2>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
              <span className="font-mono">{admin?.email}</span>
              {admin && (
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
                  style={{
                    backgroundColor:
                      admin.role === 'super_admin' ? '#7C3AED' :
                      admin.role === 'admin' ? '#1F6C4F' :
                      admin.role === 'support' ? '#0EA5E9' : '#9CA3AF',
                  }}
                >
                  {admin.role.replace('_', ' ')}
                </span>
              )}
              {admin && !admin.active && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-semibold uppercase text-red-700">
                  deactivated
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        <nav className="flex border-b border-gray-200 px-2">
          {([
            ['profile',      'Profile',          <User className="h-3.5 w-3.5" />],
            ['capabilities', 'Capabilities',     <Shield className="h-3.5 w-3.5" />],
            ['support',      'Support identity', <Mail className="h-3.5 w-3.5" />],
            ['signin',       'Sign-in',          <KeyRound className="h-3.5 w-3.5" />],
            ['audit',        'Audit log',        <Activity className="h-3.5 w-3.5" />],
          ] as Array<[TabKey, string, React.ReactNode]>).map(([k, label, icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[11px] font-medium ${
                tab === k
                  ? 'border-current text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={tab === k ? { borderColor: '#1F6C4F', color: '#1F6C4F' } : {}}
            >
              {icon}
              {label}
            </button>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading || !admin ? (
            <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
          ) : (
            <>
              {tab === 'profile'      && <ProfileTab admin={admin} />}
              {tab === 'capabilities' && <CapabilitiesTab admin={admin} />}
              {tab === 'support'      && <SupportIdentityTab admin={admin} />}
              {tab === 'signin'       && <SignInTab admin={admin} />}
              {tab === 'audit'        && <AuditTab adminId={admin.id} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Tab 1: Profile
// ─────────────────────────────────────────────────────────────────────────

function ProfileTab({ admin }: { admin: AdminDetail }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [fullName, setFullName] = useState(admin.full_name ?? '');
  const [role, setRole] = useState(admin.role);
  const [timezone, setTimezone] = useState(admin.timezone ?? '');
  const [active, setActive] = useState(admin.active);
  const [slackHandle, setSlackHandle] = useState(admin.slack_handle ?? '');
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      adminFetch(`/api/admin/team/${admin.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team-detail', admin.id] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      setError(null);
      setEdit(false);
    },
    onError: err => setError(errorMessage(err)),
  });

  if (!edit) {
    return (
      <div className="space-y-3 text-xs">
        <Row label="Full name" value={admin.full_name || '—'} />
        <Row label="Email" value={admin.email} mono />
        <Row label="Role" value={admin.role} />
        <Row label="Active" value={admin.active ? 'Yes' : 'No (deactivated)'} />
        <Row label="Timezone" value={admin.timezone || '—'} />
        <Row label="Slack handle" value={admin.slack_handle ? `@${admin.slack_handle}` : '—'} mono />
        <Row label="Last login" value={admin.last_login_at ? new Date(admin.last_login_at).toLocaleString() : 'Never'} />
        <Row label="Last login IP" value={admin.last_login_ip || '—'} mono />
        <Row label="Locked until" value={admin.locked_until ? new Date(admin.locked_until).toLocaleString() : '—'} />
        <Row label="Created" value={new Date(admin.created_at).toLocaleDateString()} />
        <button
          onClick={() => setEdit(true)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <Edit3 className="h-3 w-3" /> Edit profile
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        const changes = profileChanges(
          { full_name: admin.full_name, role: admin.role, timezone: admin.timezone, active: admin.active, slack_handle: admin.slack_handle },
          { full_name: fullName, role, timezone, active, slack_handle: slackHandle },
        );
        if (!Object.keys(changes).length) {
          setEdit(false);
          return;
        }
        update.mutate(changes);
      }}
      className="space-y-3"
    >
      <FormField label="Full name">
        <input value={fullName} onChange={e => setFullName(e.target.value)}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
      </FormField>
      <FormField label="Role">
        <select value={role} onChange={e => setRole(e.target.value)}
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
          <option value="super_admin">super_admin</option>
          <option value="admin">admin</option>
          <option value="support">support</option>
          <option value="viewer">viewer</option>
        </select>
      </FormField>
      <FormField label="Timezone">
        <input value={timezone} onChange={e => setTimezone(e.target.value)} placeholder="America/Toronto"
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
      </FormField>
      <FormField label="Slack handle">
        <input value={slackHandle} onChange={e => setSlackHandle(e.target.value)} placeholder="paul"
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
        <div className="mt-1 text-[11px] text-gray-500">Their @name in roadmap comments. Lowercase, unique.</div>
      </FormField>
      <FormField label="Status">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          Active (can sign in to admin portal)
        </label>
      </FormField>
      {error && <div className="text-xs text-red-600" role="status">{error}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={() => setEdit(false)}
          className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={update.isPending}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white"
          style={{ backgroundColor: '#1F6C4F' }}>
          <Save className="h-3 w-3" /> Save
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab 2: Capabilities
// ─────────────────────────────────────────────────────────────────────────

function CapabilitiesTab({ admin }: { admin: AdminDetail }) {
  const qc = useQueryClient();
  const { data: bundlesData } = useQuery<{ data: Bundle[] }>({
    queryKey: ['admin-team-bundles'],
    queryFn: () => adminFetch('/api/admin/team/bundles'),
  });
  const { data: catalogData } = useQuery<{ data: CapEntry[] }>({
    queryKey: ['admin-team-capabilities-catalog'],
    queryFn: () => adminFetch('/api/admin/team/capabilities-catalog'),
  });
  const bundles = bundlesData?.data ?? [];
  const catalog = catalogData?.data ?? [];

  const currentCaps = new Set(admin.permissions?.capabilities ?? []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set(currentCaps));
  const [bundlePick, setBundlePick] = useState(admin.permissions?.bundle_applied ?? '');

  // Re-sync when admin changes
  useEffect(() => { setDraft(new Set(admin.permissions?.capabilities ?? [])); }, [admin.id, admin.permissions]);

  const applyBundle = useMutation({
    mutationFn: (bundle_name: string) =>
      adminFetch(`/api/admin/team/${admin.id}/bundle/apply`, {
        method: 'POST',
        body: JSON.stringify({ bundle_name, overrides_add: [], overrides_remove: [] }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team-detail', admin.id] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      setEditing(false);
    },
  });

  const saveCustom = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/team/${admin.id}/capabilities/set`, {
        method: 'POST',
        body: JSON.stringify({ capabilities: Array.from(draft) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team-detail', admin.id] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      setEditing(false);
    },
  });

  // Group capabilities by prefix (marketing.*, support.*, etc.)
  const groups = new Map<string, CapEntry[]>();
  catalog.forEach(c => {
    const prefix = c.key.split('.')[0];
    if (!groups.has(prefix)) groups.set(prefix, []);
    groups.get(prefix)!.push(c);
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-gray-50 p-3 text-xs">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-semibold text-gray-900">
              Bundle: {admin.permissions?.bundle_applied || <span className="italic text-gray-500">custom (no bundle)</span>}
            </div>
            <div className="text-[11px] text-gray-500">
              {currentCaps.size} capabilities granted · super_admin role short-circuits all checks
            </div>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700">
              <Edit3 className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="rounded-md border border-gray-200 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Apply a bundle (replaces all capabilities)
          </div>
          <div className="mt-2 flex gap-2">
            <select value={bundlePick} onChange={e => setBundlePick(e.target.value)}
              className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs">
              <option value="">— Pick a bundle —</option>
              {bundles.map(b => (
                <option key={b.name} value={b.name}>{b.name} ({b.capability_count} caps)</option>
              ))}
            </select>
            <button
              disabled={!bundlePick || applyBundle.isPending}
              onClick={() => applyBundle.mutate(bundlePick)}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              style={{ backgroundColor: '#1F6C4F' }}>
              Apply
            </button>
          </div>
        </div>
      )}

      {editing && (
        <>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Or edit capabilities individually (super_admin only)
          </div>
          {Array.from(groups.entries()).map(([prefix, caps]) => (
            <details key={prefix} className="rounded-md border border-gray-100">
              <summary className="cursor-pointer bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">
                {prefix}.* <span className="ml-1 font-normal text-gray-400">({caps.filter(c => draft.has(c.key)).length} / {caps.length})</span>
              </summary>
              <div className="grid grid-cols-2 gap-1 p-3">
                {caps.map(c => (
                  <label key={c.key} className="flex items-center gap-1.5 text-[11px] font-mono text-gray-700">
                    <input type="checkbox" checked={draft.has(c.key)} onChange={e => {
                      const next = new Set(draft);
                      if (e.target.checked) next.add(c.key);
                      else next.delete(c.key);
                      setDraft(next);
                    }} />
                    <span className={c.high_blast ? 'text-amber-700' : ''}>{c.key.replace(`${prefix}.`, '')}</span>
                    {c.high_blast && <AlertCircle className="h-2.5 w-2.5 text-amber-500" />}
                  </label>
                ))}
              </div>
            </details>
          ))}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setDraft(new Set(currentCaps)); setEditing(false); }}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700">Cancel</button>
            <button onClick={() => saveCustom.mutate()} disabled={saveCustom.isPending}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-white"
              style={{ backgroundColor: '#1F6C4F' }}>
              Save custom capabilities ({draft.size})
            </button>
          </div>
        </>
      )}

      {!editing && (
        <div className="grid grid-cols-2 gap-1 text-[11px] font-mono">
          {[...currentCaps].sort().map(c => (
            <div key={c} className="text-gray-700 flex items-center gap-1">
              <CheckCircle2 className="h-2.5 w-2.5" style={{ color: '#1F6C4F' }} />
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab 3: Support Identity (migration 1508)
// ─────────────────────────────────────────────────────────────────────────

function SupportIdentityTab({ admin }: { admin: AdminDetail }) {
  const qc = useQueryClient();
  const [fromEmail, setFromEmail] = useState(admin.support_from_email ?? '');
  const [displayName, setDisplayName] = useState(admin.support_from_display_name ?? '');
  const [signatureHtml, setSignatureHtml] = useState(admin.support_signature_html ?? '');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/team/${admin.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          support_from_email: fromEmail.trim() || null,
          support_from_display_name: displayName.trim() || null,
          support_signature_html: signatureHtml.trim() || null,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-team-detail', admin.id] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <form onSubmit={e => { e.preventDefault(); save.mutate(); }} className="space-y-4">
      <div className="rounded-md bg-blue-50 p-3 text-[11px] text-blue-900">
        How {admin.full_name?.split(' ')[0] || 'this admin'} appears to customers in support reply emails.
        Display-name override is zero-infra (still uses support@coachcedar.com).
        Custom From email needs SendGrid sender verification — @coachcedar.com addresses work out of the box.
      </div>

      <FormField label="From: display name">
        <input value={displayName} onChange={e => setDisplayName(e.target.value)}
          placeholder='e.g. "Matt at Coach Cedar"'
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
        <p className="mt-1 text-[10px] text-gray-500">
          Customer inbox shows this as the sender name. NULL = "Coach Cedar Support".
        </p>
      </FormField>

      <FormField label="From: email address (advanced)">
        <input type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)}
          placeholder="leave blank to use support@coachcedar.com"
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
        <p className="mt-1 text-[10px] text-gray-500">
          Requires SendGrid sender verification for the address. @coachcedar.com works via existing domain auth.
        </p>
      </FormField>

      <FormField label="HTML signature (optional)">
        <textarea value={signatureHtml} onChange={e => setSignatureHtml(e.target.value)} rows={4}
          placeholder="<p>— Matt<br>Coach Cedar Customer Success</p>"
          className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-[11px]" />
        <p className="mt-1 text-[10px] text-gray-500">
          Appended to the email body before the brand footer. NULL = no signature (the email body's "agent_name" footer still appears).
        </p>
      </FormField>

      <div className="flex items-center justify-end gap-2">
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#1F6C4F' }}>
            <CheckCircle2 className="h-3 w-3" /> Saved
          </span>
        )}
        <button type="submit" disabled={save.isPending}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white"
          style={{ backgroundColor: '#1F6C4F' }}>
          <Save className="h-3 w-3" /> {save.isPending ? 'Saving…' : 'Save support identity'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab 4: Sign-in
// ─────────────────────────────────────────────────────────────────────────

function SignInTab({ admin }: { admin: AdminDetail }) {
  const sessions = useQuery({
    queryKey: ['admin-team-sessions', admin.id],
    queryFn: () => adminFetch<SessionsResponse>(`/api/admin/sessions/admin/${admin.id}`),
  });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const signOutAll = useMutation({
    mutationFn: () =>
      adminFetch<{ ended: number }>(`/api/admin/sessions/admin/${admin.id}/revoke-all`, { method: 'POST' }),
    onSuccess: res => {
      setMessage({ ok: true, text: `Signed out of ${res.ended} session${res.ended === 1 ? '' : 's'}.` });
      sessions.refetch();
    },
    onError: err => setMessage({ ok: false, text: errorMessage(err) }),
  });
  const list = sessions.data?.sessions ?? [];
  const isMe = list.some(s => s.current);

  return (
    <div className="space-y-4 text-xs">
      <div className="rounded-md bg-gray-50 p-3 text-gray-700">
        <div className="text-sm font-semibold text-gray-900">2-step verification</div>
        <div className="mt-0.5">
          The portal has no 2-step code of its own. Google sign-in uses this person's Google Workspace
          2-step verification, which is managed in the Google Workspace admin console.
        </div>
      </div>

      <div>
        <div className="mb-1 font-semibold uppercase tracking-wide text-gray-400">Open sessions</div>
        {sessions.isLoading && <div className="text-gray-500">Loading…</div>}
        {sessions.error && <div className="text-red-600">{errorMessage(sessions.error)}</div>}
        {sessions.data && (
          <SessionsList sessions={list} maxHours={sessions.data.max_hours}
            emptyText="Not signed in anywhere." />
        )}
      </div>

      {list.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-amber-900">Sign out everywhere</div>
              <div className="mt-0.5 text-[11px] text-amber-800">
                {isMe
                  ? 'Ends your other sessions. This one stays signed in.'
                  : 'Ends every session this person has. Use it if a laptop is lost or someone leaves. They can sign in again unless you also deactivate them.'}
              </div>
              <button
                onClick={() => {
                  if (window.confirm(`Sign ${admin.email} out everywhere?`)) signOutAll.mutate();
                }}
                disabled={signOutAll.isPending}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <LogOut className="h-3 w-3" />
                {signOutAll.isPending ? 'Signing out…' : 'Sign out everywhere'}
              </button>
            </div>
          </div>
        </div>
      )}
      {message && (
        <div className={message.ok ? 'text-green-700' : 'text-red-600'} role="status">{message.text}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab 5: Audit log
// ─────────────────────────────────────────────────────────────────────────

function AuditTab({ adminId }: { adminId: string }) {
  const { data, isLoading } = useQuery<{ data: AuditEntry[]; count: number }>({
    queryKey: ['admin-team-audit', adminId],
    queryFn: () => adminFetch(`/api/admin/team/${adminId}/audit?limit=100`),
  });
  const rows = data?.data ?? [];

  if (isLoading) return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center">
        <Activity className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        <div className="text-xs text-gray-500">No audit log entries for this admin.</div>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {rows.map(r => (
        <li key={r.id} className="rounded-md border border-gray-100 p-3 text-[11px]">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`font-mono ${r.success ? 'text-gray-800' : 'text-red-700'}`}>
              {r.action_type}
            </span>
            <span className="shrink-0 text-gray-500">{new Date(r.created_at).toLocaleString()}</span>
          </div>
          {r.target_resource && (
            <div className="mt-0.5 text-[10px] text-gray-500">target: <span className="font-mono">{r.target_resource}</span></div>
          )}
          {r.ip_address && (
            <div className="text-[10px] text-gray-500">ip: <span className="font-mono">{r.ip_address}</span></div>
          )}
          {r.action_details && Object.keys(r.action_details).length > 0 && (
            <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[10px] text-gray-700">
              {JSON.stringify(r.action_details, null, 2)}
            </pre>
          )}
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex gap-3">
    <span className="w-32 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    <span className={mono ? 'font-mono text-gray-800' : 'text-gray-800'}>{value}</span>
  </div>
);

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

export default AdminProfileDrawer;
